import React, { useState, useEffect } from 'react';
import { Analytics } from '@trello/atlassian-analytics';
import { Icon, IconName } from '@trello/app/common/components/controls';
import { Tooltip, Popover, usePopover, PopoverScreen } from '@trello/nachos';
import { forTemplate } from 'app/common/lib/util/i18n';
import { BoardViewsPopover, BoardViewsPopoverProps } from './BoardViewsPopover';

import styles from './BoardViewsButton.less';
import { dontUpsell } from '@trello/config';

const format = forTemplate('board-views');

enum Screen {
  BoardViews,
}

interface CombinedProps extends BoardViewsPopoverProps {
  onPublicBoard: boolean;
  boardEditable: boolean;
  isCalendarPowerUpAllowed: boolean;
  isMapPowerUpAllowed: boolean;
  isObserver: boolean;
  navigateToListView: () => boolean;
  navigateToCalendarView: () => boolean;
  navigateToMapView: () => boolean;
  preloadPlugins: () => Promise<void>;
}

export type BoardViewsButtonProps = Exclude<
  CombinedProps,
  'shouldShowCalendarOption' | 'shouldShowMapOption' | 'orgName'
>;

interface ShouldShowOptionArgs {
  isEnabled: boolean;
  isAllowed: boolean;
  onPublicBoard: boolean;
  hasReachedPowerUpLimit: boolean;
  boardEditable: boolean;
  isObserver: boolean;
}

export function shouldShowOption({
  isEnabled,
  hasReachedPowerUpLimit,
  onPublicBoard,
  isAllowed,
  boardEditable,
  isObserver,
}: ShouldShowOptionArgs) {
  if (!isEnabled) {
    if (onPublicBoard) {
      return boardEditable;
    }

    if (hasReachedPowerUpLimit) {
      return !dontUpsell;
    }

    if (!isAllowed) {
      return false;
    }
  }

  return boardEditable || isObserver || onPublicBoard;
}

export const BoardViewsButton: React.FunctionComponent<BoardViewsButtonProps> = ({
  currentView,
  onPublicBoard,
  boardEditable,
  isObserver,
  isCalendarPowerUpAllowed,
  isMapPowerUpAllowed,
  navigateToListView,
  navigateToCalendarView,
  navigateToMapView,
  preloadPlugins,
  ...boardViewsPopoverProps
}) => {
  const { toggle, hide, triggerRef, popoverProps } = usePopover<
    HTMLAnchorElement
  >({
    initialScreen: Screen.BoardViews,
  });

  const [selectedView, setSelectedView] = useState(currentView);

  useEffect(() => {
    setSelectedView(currentView);
  }, [currentView]);

  let buttonIconName;

  switch (selectedView) {
    case 'calendar':
      buttonIconName = IconName.Calendar;
      break;
    case 'map':
      buttonIconName = IconName.Location;
      break;
    case 'list':
    default:
      buttonIconName = IconName.List;
  }

  const {
    idBoard,
    isCalendarPowerUpEnabled,
    isMapPowerUpEnabled,
    hasReachedPowerUpLimit,
  } = boardViewsPopoverProps;

  const showCalendarOption = shouldShowOption({
    isEnabled: isCalendarPowerUpEnabled,
    isAllowed: isCalendarPowerUpAllowed,
    onPublicBoard,
    hasReachedPowerUpLimit,
    boardEditable,
    isObserver,
  });
  const showMapOption = shouldShowOption({
    isEnabled: isMapPowerUpEnabled,
    isAllowed: isMapPowerUpAllowed,
    onPublicBoard,
    hasReachedPowerUpLimit,
    boardEditable,
    isObserver,
  });

  // Hide the button if we have no other views available besides board view.
  if (!showCalendarOption && !showMapOption) {
    return null;
  }

  return (
    <>
      <Tooltip
        content={format('board-views')}
        delay={100}
        hideTooltipOnMouseDown
      >
        <a
          className="board-header-btn"
          ref={triggerRef}
          onClick={() => {
            Analytics.sendUIEvent({
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'boardViewsButton',
              containers: {
                board: {
                  id: idBoard,
                },
              },
              source: 'boardScreen',
            });

            toggle();
          }}
          role="button"
          onMouseOver={() => {
            preloadPlugins();
          }}
          onFocus={() => {
            preloadPlugins();
          }}
        >
          <Icon
            name={buttonIconName}
            small
            className={styles.boardViewsButtonIcon}
          />
        </a>
      </Tooltip>
      <Popover {...popoverProps} size="small" noVerticalPadding>
        <PopoverScreen id={Screen.BoardViews}>
          <BoardViewsPopover
            {...boardViewsPopoverProps}
            showCalendarOption={showCalendarOption}
            showMapOption={showMapOption}
            currentView={currentView}
            navigateToListView={() => {
              if (navigateToListView()) {
                setSelectedView('list');
              }
              hide();
            }}
            navigateToCalendarView={() => {
              if (navigateToCalendarView()) {
                setSelectedView('calendar');
              }
              hide();
            }}
            navigateToMapView={() => {
              if (navigateToMapView()) {
                setSelectedView('map');
              }
              hide();
            }}
          />
        </PopoverScreen>
      </Popover>
    </>
  );
};
